import axiosInstance from "../../config/axiosConfig";
import { useCallback, useEffect, useState } from "react";
import "../../style/Parcours/Parcours.css";
import PlanningGlobal from "./PlanningGlobal";
import PlanningParcours from "./PlanningParcours";
import ParcoursElevesPdf from "../../components/Parcours/ParcoursElevesPdf";
import PlanningGlobalPdf from "../../components/Parcours/PlanningGlobalPdf";
import {
  buildParcoursLabelMap,
  getParcoursDisplayName,
} from "../../utils/parcoursLabels";
import {
  getPlanningWeekStatusLabel,
  getPlanningWeekStatusMessage,
} from "../../utils/planningWeekStatus";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { extractApiErrorMessage } from "../../utils/apiErrorMessage";
import { copyByFallback } from "../../components/Eleves/ConventionActions";

const formatDateLabel = (date) => {
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
  }).format(date);
};

const getWeekRangeLabel = (weekStart) => {
  if (!weekStart) {
    return "Aucune semaine selectionnee";
  }

  const startDate = new Date(`${weekStart}T00:00:00`);
  if (Number.isNaN(startDate.getTime())) {
    return "Semaine invalide";
  }

  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);

  return `du ${formatDateLabel(startDate)} au ${formatDateLabel(endDate)}`;
};

function Parcours(props) {
  const semaine = props.semaine;
  const setSemaine = props.setSemaine;
  const userRole = localStorage.getItem("userRole");
  const [parcours, setParcours] = useState(null);
  const [planningWeeks, setPlanningWeeks] = useState([]);
  const [vueActive, setVueActive] = useState("globale");
  const [parcoursSelectionne, setParcoursSelectionne] = useState("");
  const [statusActionLoading, setStatusActionLoading] = useState(false);
  const [statusActionError, setStatusActionError] = useState("");
  const [elevesParcours, setElevesParcours] = useState([]);
  const [elevesParcoursLoading, setElevesParcoursLoading] = useState(false);
  const [elevesParcoursError, setElevesParcoursError] = useState("");
  const [professeurs, setProfesseurs] = useState([]);
  const [copyFeedback, setCopyFeedback] = useState("");
  const [encadrantConfirmations, setEncadrantConfirmations] = useState([]);
  const [encadrantConfirmationError, setEncadrantConfirmationError] = useState("");

  const loadPlanningWeeks = useCallback(() => {
    axiosInstance
      .get("/planningweeks")
      .then((response) => {
        const weeks = response.data || [];
        setPlanningWeeks(weeks);

        if (!semaine && weeks.length > 0 && setSemaine) {
          setSemaine(weeks[0].weekStart);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, [setSemaine, semaine]);

  useEffect(() => {
    loadPlanningWeeks();
  }, [loadPlanningWeeks]);

  useEffect(() => {
    if (!semaine) {
      setParcours({});
      return;
    }

    axiosInstance
      .get("/activiteparcours/parcours", {
        params: {
          weekStart: semaine,
        },
      })
      .then((response) => {
        setParcours(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [semaine]);

  useEffect(() => {
    if (!parcours) {
      return;
    }

    const parcoursIds = Object.keys(parcours);
    if (parcoursIds.length > 0 && !parcoursIds.includes(parcoursSelectionne)) {
      setParcoursSelectionne(parcoursIds[0]);
      return;
    }

    if (parcoursIds.length === 0) {
      setParcoursSelectionne("");
    }
  }, [parcours, parcoursSelectionne]);

  useEffect(() => {
    if (userRole !== "Admin") {
      setProfesseurs([]);
      return;
    }

    axiosInstance
      .get("/professeurs")
      .then((response) => {
        setProfesseurs(response.data || []);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [userRole]);

  useEffect(() => {
    if (userRole !== "Admin" || !parcoursSelectionne) {
      setElevesParcours([]);
      setElevesParcoursError("");
      setElevesParcoursLoading(false);
      return;
    }

    setElevesParcoursLoading(true);
    setElevesParcoursError("");

    axiosInstance
      .get(`/eleves/parcours/${parcoursSelectionne}`)
      .then((response) => {
        setElevesParcours(response.data || []);
      })
      .catch(async (error) => {
        console.error(error);

        if (error?.response?.status === 404) {
          try {
            const fallbackResponse = await axiosInstance.get("/eleves");
            const fallbackEleves = (fallbackResponse.data || []).filter(
              (eleve) =>
                String(eleve.parcoursId) === String(parcoursSelectionne)
            );

            setElevesParcours(fallbackEleves);
            setElevesParcoursError(
              "Le serveur utilise encore l'ancienne route eleves. La liste a ete chargee en mode compatible."
            );
            return;
          } catch (fallbackError) {
            console.error(fallbackError);
          }
        }

        setElevesParcours([]);
        setElevesParcoursError(
          extractApiErrorMessage(
            error,
            "La liste des eleves de ce parcours n'a pas pu etre chargee."
          )
        );
      })
      .finally(() => {
        setElevesParcoursLoading(false);
      });
  }, [parcoursSelectionne, userRole]);

  useEffect(() => {
    if (userRole !== "Admin" || !semaine) {
      setEncadrantConfirmations([]);
      setEncadrantConfirmationError("");
      return;
    }

    axiosInstance
      .get(`/planningweeks/${semaine}/encadrants/confirmations`)
      .then((response) => {
        setEncadrantConfirmations(response.data?.encadrants || []);
        setEncadrantConfirmationError("");
      })
      .catch((error) => {
        console.error(error);
        setEncadrantConfirmations([]);
        setEncadrantConfirmationError(
          extractApiErrorMessage(
            error,
            "Le suivi des confirmations encadrants n'a pas pu etre charge."
          )
        );
      });
  }, [semaine, userRole]);

  const parcoursIds = parcours ? Object.keys(parcours) : [];
  const selectedPlanningWeek =
    planningWeeks.find((planningWeek) => planningWeek.weekStart === semaine) ||
    null;
  const parcoursLabelMap = buildParcoursLabelMap(parcoursIds);
  const hasActivites =
    parcours && Object.values(parcours).some((activites) => activites.length > 0);
  const titreSemaine = semaine
    ? `Les parcours pour la semaine ${getWeekRangeLabel(semaine)}`
    : "Les parcours de la semaine";
  const activitesParcoursSelectionne = parcoursSelectionne
    ? parcours?.[parcoursSelectionne] || []
    : [];
  const professeursMap = professeurs.reduce((map, professeur) => {
    map[professeur.id] = professeur;
    return map;
  }, {});
  const elevesParcoursSelectionne = [...elevesParcours]
    .sort((leftEleve, rightEleve) => {
      const leftName = `${leftEleve.nom} ${leftEleve.prenom}`;
      const rightName = `${rightEleve.nom} ${rightEleve.prenom}`;
      return leftName.localeCompare(rightName);
    })
    .map((eleve) => ({
      ...eleve,
      tuteur: eleve.professeurId ? professeursMap[eleve.professeurId] || null : null,
    }));
  const selectedParcoursLabel = getParcoursDisplayName(
    parcoursSelectionne,
    parcoursLabelMap
  );

  const handleUpdateWeekStatus = (nextStatus) => {
    if (!semaine) {
      return;
    }

    const shouldContinue =
      nextStatus === "valide"
        ? window.confirm(
            "Voulez-vous marquer cette semaine comme validee ?"
          )
        : window.confirm(
            "Voulez-vous repasser cette semaine en brouillon ?"
          );

    if (!shouldContinue) {
      return;
    }

    setStatusActionLoading(true);
    setStatusActionError("");

    axiosInstance
      .put(`/planningweeks/${semaine}/status`, {
        status: nextStatus,
      })
      .then(() => {
        loadPlanningWeeks();
      })
      .catch((error) => {
        console.error(error);
        setStatusActionError(
          extractApiErrorMessage(
            error,
            "Le statut de la semaine n'a pas pu etre mis a jour."
          )
        );
      })
      .finally(() => {
        setStatusActionLoading(false);
      });
  };

  const encadrantsParcoursSelectionne = activitesParcoursSelectionne.reduce(
    (encadrants, item) => {
      const professeur = item?.activite?.professeur;

      if (!professeur) {
        return encadrants;
      }

      if (encadrants.some((encadrant) => encadrant.id === professeur.id)) {
        return encadrants;
      }

      return [...encadrants, professeur];
    },
    []
  );

  const elevesEmails = elevesParcoursSelectionne
    .map((eleve) => eleve.email)
    .filter(Boolean);
  const encadrantsEmails = encadrantsParcoursSelectionne
    .map((encadrant) => encadrant.email)
    .filter(Boolean);
  const weeklyEncadrantEmails = encadrantConfirmations
    .map((encadrant) => encadrant.email)
    .filter(Boolean);

  const handleCopyEmails = async (emails, targetLabel) => {
    if (emails.length === 0) {
      setCopyFeedback(`Aucun email ${targetLabel} n'est disponible pour ce parcours.`);
      return;
    }

    const textToCopy = emails.join("; ");

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        await copyByFallback(textToCopy);
      }

      setCopyFeedback(
        `Les emails ${targetLabel} ont ete copies pour ${selectedParcoursLabel}.`
      );
    } catch (error) {
      console.error(error);
      setCopyFeedback(
        `La copie des emails ${targetLabel} n'a pas pu etre finalisee.`
      );
    }
  };

  return (
    <div className="parcours-page">
      <div className="parcours-header">
        <h1>{titreSemaine}</h1>
        <p className="parcours-subtitle">
          Visualisez la semaine dans une vue globale des activites ou dans une
          vue centree sur un seul parcours.
        </p>
      </div>

      <div className="parcours-week-picker">
        <label htmlFor="planning-week-start">
          Choisir la semaine a afficher :
        </label>
        <input
          id="planning-week-start"
          type="date"
          value={semaine}
          onChange={(e) => setSemaine?.(e.target.value)}
        />
        <p className="parcours-week-help">
          Semaine affichee : {getWeekRangeLabel(semaine)}
        </p>
        {planningWeeks.length > 0 && (
          <p className="parcours-week-help">
            Semaines generees : {planningWeeks.length}
          </p>
        )}
      </div>

      {selectedPlanningWeek && (
        <section className="planning-week-status-card">
          <div>
            <p className="planning-week-status-tag">
              {getPlanningWeekStatusLabel(selectedPlanningWeek.status)}
            </p>
            <h2>Etat de la semaine</h2>
            <p className="planning-week-status-text">
              {getPlanningWeekStatusMessage(selectedPlanningWeek)}
            </p>
          </div>
          <div className="planning-week-status-actions">
            {selectedPlanningWeek.status !== "valide" ? (
              <button
                className="btn"
                type="button"
                disabled={statusActionLoading}
                onClick={() => handleUpdateWeekStatus("valide")}
              >
                Marquer comme validee
              </button>
            ) : (
              <button
                className="btn btn-secondary"
                type="button"
                disabled={statusActionLoading}
                onClick={() => handleUpdateWeekStatus("brouillon")}
              >
                Repasser en brouillon
              </button>
            )}
          </div>
          {statusActionError && (
            <p className="generation-feedback is-error">{statusActionError}</p>
          )}
        </section>
      )}

      {userRole === "Admin" && semaine && (
        <section className="parcours-contacts-card">
          <div className="parcours-contacts-header">
            <div>
              <p className="planning-week-status-tag">Suivi encadrants</p>
              <h2>Confirmation des plannings encadrants</h2>
              <p className="planning-week-status-text">
                Suivez ici les encadrants qui ont confirme leur planning de la
                semaine avant l'envoi des mails definitifs.
              </p>
            </div>
            <button
              className="btn"
              type="button"
              onClick={() => handleCopyEmails(weeklyEncadrantEmails, "des encadrants de la semaine")}
            >
              Copier tous les emails encadrants
            </button>
          </div>

          {encadrantConfirmationError && (
            <p className="generation-feedback is-error">
              {encadrantConfirmationError}
            </p>
          )}

          {encadrantConfirmations.length > 0 ? (
            <div className="parcours-contacts-grid">
              {encadrantConfirmations.map((encadrant) => (
                <article className="generation-summary-card" key={encadrant.id}>
                  <span className="generation-summary-label">
                    {encadrant.isConfirmed ? "Confirme" : "En attente"}
                  </span>
                  <strong>
                    {encadrant.prenom} {encadrant.nom}
                  </strong>
                  <p>{encadrant.email}</p>
                  <p>
                    {encadrant.activityCount} activite(s), {encadrant.parcoursCount} parcours, {encadrant.momentCount} creneau(x).
                  </p>
                  <p>
                    {encadrant.isConfirmed && encadrant.confirmedAt
                      ? `Confirme le ${new Intl.DateTimeFormat("fr-FR", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }).format(new Date(encadrant.confirmedAt))}`
                      : "Confirmation en attente pour cette semaine."}
                  </p>
                </article>
              ))}
            </div>
          ) : (
            <div className="internal-empty">
              Aucun encadrant n'est encore implique sur cette semaine.
            </div>
          )}
        </section>
      )}

      <div className="parcours-view-switch">
        <button
          className={`btn ${vueActive === "globale" ? "is-active" : ""}`}
          onClick={() => setVueActive("globale")}
          type="button"
        >
          Vue globale
        </button>
        <button
          className={`btn ${vueActive === "parcours" ? "is-active" : ""}`}
          onClick={() => setVueActive("parcours")}
          type="button"
        >
          Vue parcours
        </button>
      </div>

      {vueActive === "parcours" && (
        <>
          <div className="parcours-selection-bar">
            <div className="parcours-selection-zone">
              <label htmlFor="parcours-select">Choisir un parcours :</label>
              <select
                id="parcours-select"
                value={parcoursSelectionne}
                onChange={(e) => {
                  setParcoursSelectionne(e.target.value);
                  setCopyFeedback("");
                }}
              >
                {parcoursIds.map((parcoursId) => (
                  <option key={parcoursId} value={parcoursId}>
                    {getParcoursDisplayName(parcoursId, parcoursLabelMap)}
                  </option>
                ))}
              </select>
            </div>

            {userRole === "Admin" &&
              parcoursSelectionne &&
              (elevesParcoursLoading ? (
                <button
                  className="btn parcours-download-link"
                  type="button"
                  disabled
                >
                  Chargement des eleves...
                </button>
              ) : (
              <>
              <PDFDownloadLink
                className="btn parcours-download-link"
                document={
                  <ParcoursElevesPdf
                    parcoursLabel={selectedParcoursLabel}
                    semaineLabel={getWeekRangeLabel(semaine)}
                    eleves={elevesParcoursSelectionne}
                    activites={activitesParcoursSelectionne}
                  />
                }
                fileName={`eleves-${selectedParcoursLabel
                  .replace(/\s+/g, "-")
                  .toLowerCase()}.pdf`}
              >
                {({ loading }) =>
                  loading
                    ? "Preparation du PDF..."
                    : "Telecharger les eleves du parcours"
                }
              </PDFDownloadLink>
              <PDFDownloadLink
                className="btn parcours-download-link"
                document={
                  <PlanningGlobalPdf
                    parcours={{ [parcoursSelectionne]: activitesParcoursSelectionne }}
                    parcoursLabelMap={parcoursLabelMap}
                    semaineLabel={getWeekRangeLabel(semaine)}
                  />
                }
                fileName={`planning-${selectedParcoursLabel
                  .replace(/\s+/g, "-")
                  .toLowerCase()}.pdf`}
              >
                {({ loading }) =>
                  loading
                    ? "Preparation du PDF..."
                    : "Telecharger le planning du parcours"
                }
              </PDFDownloadLink>
              </>
              ))}
          </div>

          {userRole === "Admin" && elevesParcoursError && (
            <p className="eleve-feedback is-error">{elevesParcoursError}</p>
          )}

          {userRole === "Admin" && parcoursSelectionne && (
            <section className="parcours-contacts-card">
              <div className="parcours-contacts-header">
                <div>
                  <p className="planning-week-status-tag">
                    Communication parcours
                  </p>
                  <h2>{selectedParcoursLabel}</h2>
                  <p className="planning-week-status-text">
                    Recuperez les emails utiles pour confirmer le planning avec
                    les encadrants puis transmettre le programme aux eleves.
                  </p>
                </div>
              </div>

              <div className="parcours-contacts-grid">
                <article className="generation-summary-card">
                  <span className="generation-summary-label">Eleves</span>
                  <strong>{elevesParcoursSelectionne.length}</strong>
                  <p>
                    {elevesParcoursLoading
                      ? "Chargement des emails eleves..."
                      : elevesEmails.length > 0
                      ? elevesEmails.join("; ")
                      : "Aucun email eleve n'est disponible pour ce parcours."}
                  </p>
                  <button
                    className="btn"
                    type="button"
                    disabled={elevesParcoursLoading}
                    onClick={() => handleCopyEmails(elevesEmails, "des eleves")}
                  >
                    Copier les emails des eleves
                  </button>
                </article>

                <article className="generation-summary-card">
                  <span className="generation-summary-label">Encadrants</span>
                  <strong>{encadrantsParcoursSelectionne.length}</strong>
                  <p>
                    {encadrantsEmails.length > 0
                      ? encadrantsEmails.join("; ")
                      : "Aucun email encadrant n'est disponible pour ce parcours."}
                  </p>
                  <button
                    className="btn"
                    type="button"
                    onClick={() =>
                      handleCopyEmails(encadrantsEmails, "des encadrants")
                    }
                  >
                    Copier les emails des encadrants
                  </button>
                </article>
              </div>

              {copyFeedback && (
                <p className="generation-feedback is-info">{copyFeedback}</p>
              )}
            </section>
          )}
        </>
      )}

      <div className="parcours-content-zone">
        {vueActive === "globale" && hasActivites && userRole === "Admin" && (
          <div className="parcours-selection-bar">
            <PDFDownloadLink
              className="btn parcours-download-link"
              document={
                <PlanningGlobalPdf
                  parcours={parcours}
                  parcoursLabelMap={parcoursLabelMap}
                  semaineLabel={getWeekRangeLabel(semaine)}
                />
              }
              fileName={`planning-global-${semaine || "semaine"}.pdf`}
            >
              {({ loading }) =>
                loading ? "Preparation du PDF..." : "Telecharger le planning global"
              }
            </PDFDownloadLink>
          </div>
        )}

        {vueActive === "globale" &&
          (hasActivites ? (
            <PlanningGlobal
              parcours={parcours}
              parcoursLabelMap={parcoursLabelMap}
            />
          ) : (
            <section className="parcours-placeholder-zone">
              <h2>Planning global des activites</h2>
              <p>
                Le tableau apparaitra ici des qu&apos;au moins une activite sera
                associee a un parcours depuis la fiche d&apos;une activite.
              </p>
            </section>
          ))}

        {vueActive === "parcours" && (
          parcoursSelectionne ? (
            <PlanningParcours
              parcours={parcours}
              parcoursSelectionne={parcoursSelectionne}
              parcoursLabelMap={parcoursLabelMap}
            />
          ) : (
            <section className="parcours-placeholder-zone">
              <h2>Planning par parcours</h2>
              <p>
                Choisissez un parcours pour afficher son emploi du temps dans le
                tableau.
              </p>
            </section>
          )
        )}

        {parcoursIds.length === 0 && (
          <section className="parcours-placeholder-zone">
            <h2>Aucun parcours exploitable pour le moment</h2>
            <p>
              Aucun parcours n&apos;est encore pret a etre affiche. Verifiez la
              generation des parcours puis associez au moins une activite.
            </p>
          </section>
        )}
      </div>
    </div>
  );
}

export default Parcours;
